import type { BookableCandidateDto } from "@repo/api";
import {
  type AvailabilityBlockDto,
  bookCuratedSession,
  useAvailabilityBlocks,
  useCase,
  useLocations,
  usePractitioner,
  useSchedules,
  useSlots,
  useVisits,
  type VisitDto,
} from "@repo/data-store";
import { mintEntityId } from "@repo/domain";
import { endOfDay, format, isToday, parseISO, startOfDay } from "date-fns";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useFindCuratedSessionCandidates } from "@/features/scheduling/hooks/use-find-bookable-candidates";
import { expandRecurringBlock, parseSchedulingParams } from "@/features/scheduling/lib/schedule/availability";
import { createSubmitOnce, type SubmitOnce } from "@/shared/forms/submit-once";
import { practitionerDisplayName } from "@/shared/lib/practitioner-utils";
import { ScheduleSessionsContext, type ScheduleSessionsContextValue } from "./context";
import { deviceBandsForDay } from "./device-bands";
import type {
  Assignment,
  RailAppointment,
  RailBlock,
  RailSelection,
  ScheduleMode,
  SessionBookingTarget,
} from "./types";

/**
 * A candidate's booked span. The candidate carries it directly now — its
 * placements are the individual calendar claims WITHIN that span, and the
 * earliest is not required to be the practitioner's.
 */
function candidateInterval(c: BookableCandidateDto): { start: string; end: string } {
  return { start: c.start, end: c.end };
}

/** A candidate's room = the Location it claims (sessions are single-room). */
function candidateRoom(c: BookableCandidateDto): string | undefined {
  return c.placements.find((p) => p.resourceType === "Location")?.resourceId;
}

/** Every Device the candidate claims — what the rail overlays as busy bands. */
function candidateDeviceIds(c: BookableCandidateDto): string[] {
  return c.placements.flatMap((p) => (p.resourceType === "Device" ? [p.resourceId] : []));
}

/** Blocked (one-time + expanded recurring availability blocks) intervals overlapping
 *  `date` — the rail's hatched "off / unavailable" context. Booked appointments are
 *  rendered separately as cards, not here. */
function blockedBandsForDay(date: Date, blocks: AvailabilityBlockDto[]): RailBlock[] {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);
  const out: RailBlock[] = [];
  for (const b of blocks) {
    const label = b.reason;
    if (b.blockType === "recurring") {
      for (const e of expandRecurringBlock(b, dayStart, dayEnd)) out.push({ ...e, ...(label ? { label } : {}) });
    } else if (b.start && b.end) {
      const start = new Date(b.start);
      const end = new Date(b.end);
      if (start < dayEnd && end > dayStart) out.push({ start, end, ...(label ? { label } : {}) });
    }
  }
  return out;
}

/** Existing booked appointments on the care-manager schedule overlapping `date` —
 *  rendered as cards so a real booking reads as an appointment, not blocked time. */
function appointmentsForDay(date: Date, visits: VisitDto[]): RailAppointment[] {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);
  const out: RailAppointment[] = [];
  for (const v of visits) {
    if (!v.start || !v.end) continue;
    const start = new Date(v.start);
    const end = new Date(v.end);
    if (start < dayEnd && end > dayStart) {
      out.push({
        start,
        end,
        title: v.protocolTitle ?? v.description ?? "Booked",
        patientName: v.patientName,
        practitionerName: v.practitionerName,
      });
    }
  }
  return out;
}

export interface ScheduleSessionsProviderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  caseId: string;
  sessions: SessionBookingTarget[];
  children: ReactNode;
}

/**
 * Owns the multi-session scheduling state machine and exposes it through
 * `ScheduleSessionsContext`. The practitioner is fixed to the case's care
 * manager (assigning one is a gate on plan apply), so each session's flow is
 * date → place a slot → confirm; placements accumulate until every session has
 * one, then a single confirm books them sequentially.
 */
export function ScheduleSessionsProvider({
  open,
  onOpenChange,
  patientId,
  caseId,
  sessions,
  children,
}: ScheduleSessionsProviderProps) {
  // Booking-specific reads stay dormant until the dialog opens — the provider is
  // always mounted (one per applied-plan view), so gating on `open` avoids eagerly
  // syncing the schedule/practitioner collections for plans no one is scheduling.
  const caseModel = useCase(open ? caseId : undefined);
  const practitionerId = caseModel?.careManagerPractitionerId;
  const { practitioner } = usePractitioner(practitionerId);
  const actorRef = practitionerId ? `Practitioner/${practitionerId}` : undefined;
  const { schedules, isLoading: isLoadingSchedule } = useSchedules(actorRef);
  const schedule = schedules?.[0];
  const scheduleId = schedule?.scheduleId;
  const { locations } = useLocations();
  // Care-manager context for the rail (dormant until a schedule resolves): existing
  // booked appointments render as cards; availability blocks render as hatched bands.
  const { visits } = useVisits(scheduleId ? { scheduleId } : undefined);
  const { blocks } = useAvailabilityBlocks(scheduleId);
  // Device schedules (one per device) so the session's required devices' booked time
  // shows on the rail — a device can be busy on another practitioner's appointment.
  const { schedules: allSchedules } = useSchedules();
  const deviceMetaByScheduleId = useMemo(() => {
    const map = new Map<string, { deviceId: string; name: string }>();
    for (const s of allSchedules) {
      if (s.actor.type === "Device") map.set(s.scheduleId, { deviceId: s.actor.id, name: s.actor.display ?? "Device" });
    }
    return map;
  }, [allSchedules]);
  const deviceScheduleIds = useMemo(() => [...deviceMetaByScheduleId.keys()], [deviceMetaByScheduleId]);
  const { slots: deviceBusySlots } = useSlots(scheduleId ? deviceScheduleIds : [], { status: "busy" });

  const [activeIdx, setActiveIdx] = useState(0);
  const [assignments, setAssignments] = useState<Record<string, Assignment>>({});
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [placedCandidate, setPlacedCandidate] = useState<BookableCandidateDto | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [mode, setMode] = useState<ScheduleMode>("scheduling");
  const [busy, setBusy] = useState(false);
  const submissions = useRef(new Map<string, SubmitOnce>());
  const [advancing, setAdvancing] = useState(false);

  const {
    candidates: dayCandidates,
    isLoading: isLoadingSlots,
    error: slotsError,
    query,
    reset: resetSlots,
  } = useFindCuratedSessionCandidates();
  const lastQueryKey = useRef("");
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeSession = sessions[activeIdx];
  const allAssigned = sessions.length > 0 && sessions.every((s) => assignments[s.orderSetId]);
  const assignedCount = Object.keys(assignments).length;

  // ── Reset transient state whenever the dialog closes ──
  // Also cancel any pending auto-advance so it can't fire setState onto the
  // just-reset state and flash a stale session/date after close.
  useEffect(() => {
    if (open) return;
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
    submissions.current.clear();
    setActiveIdx(0);
    setAssignments({});
    setSelectedDate(undefined);
    setPlacedCandidate(null);
    setSelectedRoom(null);
    setMode("scheduling");
    setBusy(false);
    setAdvancing(false);
    resetSlots();
    lastQueryKey.current = "";
  }, [open, resetSlots]);

  // Clear a pending auto-advance timer on unmount.
  useEffect(() => () => clearTimeout(advanceTimer.current ?? undefined), []);

  // ── Fetch candidates when the active session + date changes ──
  //
  // The set is the only thing named. Which calendars are involved, and which
  // practitioner anchors the search, are read from it server-side — the query no
  // longer passes `scheduleIds`, and could not: a session's rooms and devices are
  // resolved from its own frozen resource requirements.
  useEffect(() => {
    if (!scheduleId || !selectedDate || !activeSession) return;
    const key = `${activeSession.orderSetId}|${selectedDate.toISOString()}`;
    if (key === lastQueryKey.current) return;
    lastQueryKey.current = key;
    query({
      orderSetId: activeSession.orderSetId,
      start: (isToday(selectedDate) ? new Date() : startOfDay(selectedDate)).toISOString(),
      end: endOfDay(selectedDate).toISOString(),
    });
  }, [scheduleId, selectedDate, activeSession, query]);

  const value = useMemo<ScheduleSessionsContextValue>(() => {
    const allCandidates = dayCandidates;
    const assignedDates = Object.values(assignments).map((a) => parseISO(a.candidate.start));
    const dayAvailability = parseSchedulingParams(schedule);
    const busyBands = selectedDate ? blockedBandsForDay(selectedDate, blocks) : [];
    const appointments = selectedDate ? appointmentsForDay(selectedDate, visits) : [];
    // Devices the day's candidates resolve to — only these are relevant to overlay.
    const requiredDeviceIds = new Set(allCandidates.flatMap(candidateDeviceIds));
    const deviceBands = selectedDate
      ? deviceBandsForDay(selectedDate, deviceBusySlots, deviceMetaByScheduleId, requiredDeviceIds, appointments)
      : [];

    // Our own already-chosen sessions landing on this date (excluding the active one,
    // which is shown live as the placed candidate) — rendered as blocks on the rail.
    const selections: RailSelection[] =
      selectedDate == null
        ? []
        : sessions.flatMap((s, i) => {
            if (i === activeIdx) return [];
            const a = assignments[s.orderSetId];
            if (!a) return [];
            const { start, end } = candidateInterval(a.candidate);
            const startD = parseISO(start);
            if (startD < startOfDay(selectedDate) || startD > endOfDay(selectedDate)) return [];
            return [{ start: startD, end: parseISO(end), title: s.sessionTitle }];
          });

    // Rooms offered across the day's candidates, resolved to display names.
    const nameById = new Map<string, string>(locations.map((l) => [l.locationId, l.name ?? "Room"]));
    const roomIds = [...new Set(allCandidates.map(candidateRoom).filter((id): id is string => !!id))];
    const rooms = roomIds.map((id) => ({ id, name: nameById.get(id) ?? "Room" }));

    // Active candidates = the day's candidates filtered to the chosen room.
    const candidates = selectedRoom ? allCandidates.filter((c) => candidateRoom(c) === selectedRoom) : allCandidates;

    // Ordered, non-overlapping window for the active session: it must start
    // at/after the latest-ending earlier session and end at/before the
    // earliest-starting later session (by session index). This enforces the
    // sequence and makes two sessions sharing a slot impossible.
    let minStart: string | null = null;
    let maxEnd: string | null = null;
    sessions.forEach((s, i) => {
      if (i === activeIdx) return;
      const a = assignments[s.orderSetId];
      if (!a) return;
      const { start, end } = candidateInterval(a.candidate);
      if (i < activeIdx) {
        if (minStart === null || Date.parse(end) > Date.parse(minStart)) minStart = end;
      } else if (maxEnd === null || Date.parse(start) < Date.parse(maxEnd)) {
        maxEnd = start;
      }
    });
    const bounds = { minStart, maxEnd };
    const withinBounds = (candidate: BookableCandidateDto) => {
      const { start, end } = candidateInterval(candidate);
      if (minStart !== null && Date.parse(start) < Date.parse(minStart)) return false;
      if (maxEnd !== null && Date.parse(end) > Date.parse(maxEnd)) return false;
      return true;
    };
    const careManagerName = practitionerDisplayName(practitioner) ?? "Case care manager";

    function switchToGroup(idx: number) {
      if (idx === activeIdx) return;
      const existing = assignments[sessions[idx]?.orderSetId ?? ""];
      setActiveIdx(idx);
      if (existing) {
        setSelectedDate(parseISO(existing.candidate.start));
        setPlacedCandidate(existing.candidate);
        setSelectedRoom(candidateRoom(existing.candidate) ?? null);
      } else {
        setSelectedDate(undefined);
        setPlacedCandidate(null);
        setSelectedRoom(null);
      }
      resetSlots();
      lastQueryKey.current = "";
    }

    function selectDate(date: Date | undefined) {
      if (!date) return;
      setSelectedDate(date);
      setPlacedCandidate(null);
      resetSlots();
      lastQueryKey.current = "";
    }

    function setRoom(roomId: string | null) {
      setSelectedRoom(roomId);
      setPlacedCandidate(null);
    }

    /** Place (highlight) a candidate on the active session — does not commit. */
    function placeCandidate(candidate: BookableCandidateDto) {
      if (!activeSession || !withinBounds(candidate)) return;
      setPlacedCandidate(candidate);
    }

    function clearActiveTime() {
      if (!activeSession || busy || advancing) return;
      setPlacedCandidate(null);
      setAssignments((current) => {
        const next = { ...current };
        delete next[activeSession.orderSetId];
        return next;
      });
    }

    /** Commit the placed candidate and advance to the next unassigned session. */
    function confirmActive() {
      if (!activeSession || !placedCandidate) return;
      const start = placedCandidate.start;
      const next: Record<string, Assignment> = {
        ...assignments,
        [activeSession.orderSetId]: {
          candidate: placedCandidate,
          dateLabel: format(parseISO(start), "EEE, MMM d"),
          timeLabel: format(parseISO(start), "h:mm a"),
        },
      };
      setAssignments(next);

      const nextIdx = sessions.findIndex((s, i) => i > activeIdx && !next[s.orderSetId]);
      if (nextIdx === -1) {
        setPlacedCandidate(null);
        return;
      }
      setAdvancing(true);
      advanceTimer.current = setTimeout(() => {
        advanceTimer.current = null;
        setActiveIdx(nextIdx);
        setSelectedDate(undefined);
        setPlacedCandidate(null);
        setSelectedRoom(null);
        resetSlots();
        lastQueryKey.current = "";
        setAdvancing(false);
      }, 340);
    }

    async function book() {
      if (!allAssigned || busy) return;
      setBusy(true);
      let failed = 0;
      for (const session of sessions) {
        const a = assignments[session.orderSetId];
        if (!a) continue;
        try {
          // Only the set crosses the wire. `patientId` is the optimistic row's,
          // and the action does not send it — the server reads it off the set,
          // so a client cannot misattribute the visit.
          let once = submissions.current.get(session.orderSetId);
          if (!once) {
            once = createSubmitOnce(mintEntityId);
            submissions.current.set(session.orderSetId, once);
          }
          await once(
            (id) =>
              bookCuratedSession({
                id,
                candidate: a.candidate,
                orderSetId: session.orderSetId,
                patientId,
              }).isPersisted.promise,
          );
        } catch {
          failed++;
        }
      }
      if (failed === 0) {
        toast.success(`${sessions.length} session${sessions.length !== 1 ? "s" : ""} booked.`);
      }
      setBusy(false);
      if (failed === 0) onOpenChange(false);
    }

    return {
      state: {
        open,
        sessions,
        activeIdx,
        activeSession,
        assignments,
        assignedCount,
        allAssigned,
        selectedDate,
        placedCandidate,
        mode,
        busy,
        advancing,
        candidates,
        isLoadingSlots,
        slotsError,
        rooms,
        selectedRoom,
        hasSchedule: !!scheduleId,
        isLoadingSchedule,
        dayAvailability,
        busyBands,
        appointments,
        deviceBands,
        selections,
        practitionerName: careManagerName,
        assignedDates,
        bounds,
      },
      actions: {
        setOpen: (next) => !busy && onOpenChange(next),
        switchToGroup,
        selectDate,
        placeCandidate,
        clearActiveTime,
        setRoom,
        confirmActive,
        goToReview: () => allAssigned && setMode("review"),
        backToScheduling: () => setMode("scheduling"),
        book,
      },
    };
  }, [
    open,
    sessions,
    activeIdx,
    activeSession,
    assignments,
    assignedCount,
    allAssigned,
    selectedDate,
    placedCandidate,
    selectedRoom,
    mode,
    busy,
    advancing,
    dayCandidates,
    isLoadingSlots,
    slotsError,
    locations,
    scheduleId,
    isLoadingSchedule,
    schedule,
    visits,
    blocks,
    deviceBusySlots,
    deviceMetaByScheduleId,
    practitioner,
    patientId,
    onOpenChange,
    resetSlots,
  ]);

  return <ScheduleSessionsContext value={value}>{children}</ScheduleSessionsContext>;
}
